import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { CircleArrowLeft, PencilIcon, PlusIcon, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useDeleteTransactionCode } from '~entities/payment/api/delete-transaction-code';
import {
  type TransactionCode,
  useGetTransactionCode,
} from '~entities/payment/api/get-transaction-codes';

import { TransactionCodesForm } from '~features/payment/ui/transaction-codes-form';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';

const columnHelper = createColumnHelper<TransactionCode>();

export const TransactionCodes = () => {
  const cols = [
    columnHelper.accessor('code', {
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Transaction Code Name'
        />
      ),
      cell: (info) => <div className='px-4 capitalize'>{info.getValue()}</div>,
    }),
    columnHelper.accessor('description', {
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Description'
        />
      ),
      cell: (info) => (
        <div className='px-4'>
          {info.getValue().length > 0
            ? info.getValue().charAt(0).toUpperCase() + info.getValue().slice(1)
            : ''}
        </div>
      ),
    }),
    columnHelper.accessor('entities', {
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Assigned Classes/Events'
        />
      ),
      cell: (info) => <div className='px-4'>{info.getValue().length}</div>,
    }),
    columnHelper.display({
      id: 'actions',
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Actions'
          disableHiding
          disableSorting
        />
      ),
      cell: (info) => {
        const code = info.row.original;
        return (
          <div className='flex justify-center gap-2 px-4'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                handleOpenForm(code);
              }}
              className='h-fit p-0'
            >
              <PencilIcon className='size-4' />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant='ghost' size='sm' className='h-fit p-0'>
                  <Trash2 className='size-4' color='red' />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    this Transaction Code.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      void handleDelete(code._id);
                    }}
                  >
                    Continue
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        );
      },
    }),
  ];

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [currentCode, setCurrentCode] = useState<TransactionCode | null>(null);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const { data, isFetching } = useGetTransactionCode();

  const mutation = useDeleteTransactionCode();

  const handleOpenForm = (code: TransactionCode) => {
    setCurrentCode(code);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setCurrentCode(null);
  };

  const handleDelete = async (id: string) => {
    try {
      await mutation.mutateAsync({ id });
      toast.success('Transaction Code deleted successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete transaction code');
    }
  };

  return (
    <div>
      <Link to='/dashboard' className='flex w-max items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Transaction Codes</h2>
      </Link>

      <div className='container mx-auto mt-8 border-t'>
        <div className='my-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end'>
          <Button
            onClick={() => {
              setIsFormOpen(true);
            }}
            className='flex w-full items-center gap-1 sm:w-auto'
          >
            <PlusIcon className='size-4' />
            <span>New Transaction Code</span>
          </Button>
        </div>

        <EnrollioTable
          ariaLabel='Transaction codes'
          totalLabel='Codes'
          filterHeader={(table) => (
            <EnrollioTableSearch
              placeholder='Search transaction codes...'
              value={
                (table.getColumn('code')?.getFilterValue() as
                  | string
                  | undefined) ?? ''
              }
              onChange={(value) => {
                table.getColumn('code')?.setFilterValue(value);
              }}
            />
          )}
          toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
          columns={cols}
          data={data ?? []}
          isUpdating={isFetching}
          sorting={sorting}
          onSortingChange={setSorting}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
        />
        {/* </Card> */}

        <TransactionCodesForm
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          transactionCode={currentCode}
        />
      </div>
    </div>
  );
};
